import "./styles.css";
import { FC, useState } from "react";
import { useFormik } from "formik";
import { Box, Grid, InputLabel, TextField } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import useMutation from "../../../../../shared/components/hooks/useMutation";
import useQuery from "../../../../../shared/components/hooks/useQuery";
import InputTextfield from "../../../../../shared/components/textField";
import { FormContainer } from "../../../../../shared/components/formContainer";
import { ViewContainer } from "../../../../../shared/components/viewContainer";
import SingleEntityContainer from "../../../../../shared/components/singleEntityContainer";
import { TitleBarProps } from "../../../../../shared/components/titleBar";
import { getTextFields } from "../variables/textFields";
import { customizeValidationSchema } from "../variables/customSchema";
import { Industry } from "../../../../../shared/interfaces/industry";
import ToggleSwitch from "../../../../../shared/components/switch";
import SingleEntityImageViewer from "../../../../../shared/components/singleEntityImageViewer";
import { EditButton } from "../../../../../shared/components/editButton";
import { CloseButton } from "../../../../../shared/components/closeButton";
import { ProfileBorderBox } from "../../../../../shared/components/profileComponents";

const IndustryEditView: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loading, data, fetchData } = useQuery(`/industry/${id}`, null);
  const { loading: apiLoading, modifyData } = useMutation();
  const [editStatus, setEditStatus] = useState(false);
  const industryData: Industry = (data as any)?.data || {};
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  //Edit permission checking
  const entityPermissions: any = (data as any)?.entityPermissions;
  const editPermission = entityPermissions
    ? entityPermissions?.includes("Industry-Update")
    : true;
  //End of edit permission checking

  const initialValues = {
    name: industryData?.name ?? "",
    enabled: industryData?.enabled ? true : false,
  };

  const industryForm = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validateOnBlur: true,

    validationSchema: customizeValidationSchema(),

    onSubmit: async (formData: any) => {
      let response;
      if (id) {
        const formDataIndustry = new FormData();
        if (selectedFile) {
          formDataIndustry.append("file", selectedFile);
        }
        formDataIndustry.append("name", formData.name);
        formDataIndustry.append("enabled", formData.enabled ? "true" : "false");
        response = await modifyData(
          `/industry/${id}`,
          "put",
          formDataIndustry,
          industryForm
        );
      }
      if (response) {
        closeUserEdit();
      }
    },
  });

  const closeUserEdit = () => {
    fetchData();
    setEditStatus(false);
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    setSelectedFile(file);

    if (file) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }

    industryForm.setFieldValue("iconURL", file || "");
  };

  const editTitlebar: TitleBarProps = {
    headerTitle: "Industry -  Edit",
    title: "Edit Industry",
    buttonTitle: "Save",
    onClick: () => industryForm.handleSubmit(),
    dirty: industryForm.dirty,
    buttonLoading: apiLoading,
    editCloseIcon: {
      visible: true,
      onClick: () => closeUserEdit(),
    },
  };

  const viewTitleBar: TitleBarProps = {
    title: "Industry Details",
    headerTitle: "Industry Details",
  };
  const industryEditFields = getTextFields(industryData);

  const closeDetailsView = () => {
    navigate("/master-datas/industries");
  };

  const industryEditTooltipNode = (
    <>
      {editPermission && <EditButton onClick={() => setEditStatus(true)} /> }
      <CloseButton onClick={() => closeDetailsView()} />
    </>
  );

  return (
    <>
      {editStatus && (
        <>
          <ProfileBorderBox>
            <FormContainer titleBar={editTitlebar}>
              <Box className="form-border-custom">
                <Grid
                  container
                  alignItems="stretch"
                  columnSpacing={4}
                  rowSpacing={2}
                >
                  <Grid item xs={12}>
                    <ToggleSwitch
                      status={industryForm?.values?.enabled}
                      handleChange={() =>
                        industryForm.setFieldValue(
                          "enabled",
                          !industryForm?.values?.enabled
                        )
                      }
                    />
                  </Grid>
                  {industryEditFields.map(
                    (field: any, index: number) =>
                      (field?.formField === undefined ||
                        field.formField === true) && (
                        <Grid item md={6} sm={6} xs={12} key={field.id}>
                          <InputTextfield
                            form={industryForm}
                            fieldProps={field}
                            key={index}
                          />
                        </Grid>
                      )
                  )}
                  <Grid item md={6} sm={6} xs={6} key="industry-iconURL">
                    <InputLabel>Icon</InputLabel>
                    <TextField
                      size="small"
                      fullWidth
                      id="outlined-basic"
                      type={"file"}
                      sx={{
                        marginTop: "1vh",
                        "& .MuiInputBase-root": {
                          height: "40px",
                          fontSize: "1rem",
                        },
                      }}
                      onChange={handleFileChange}
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={6} key="industry-iconPreview">
                    <div>
                      <SingleEntityImageViewer
                        src={previewUrl ? previewUrl : industryData?.iconUrl}
                        alt="Event"
                      />
                    </div>
                  </Grid>
                </Grid>
              </Box>
            </FormContainer>
          </ProfileBorderBox>
        </>
      )}

      {!editStatus && (
        <ProfileBorderBox>
          <ViewContainer loading={loading} titleBar={viewTitleBar}>
            <div className="entity-container">
              <SingleEntityContainer
                entityFields={industryEditFields}
                tooltips={industryEditTooltipNode}
              ></SingleEntityContainer>
            </div>
          </ViewContainer>
        </ProfileBorderBox>
      )}
    </>
  );
};

export default IndustryEditView;
